import { DeleteLimitDialog } from "@/modules/dashboard/components/DeleteLimitDialog/DeleteLimitDialog";
import { Button } from "@/modules/shadcn/ui/button";
import { Skeleton } from "@/modules/shadcn/ui/skeleton";
import { ModelStatusEnum } from "@repo/schema";
import { Copy, Square, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import type { AppliedAugmentation } from "../AugmentationSettings/augmentationTypes";
import type { DuplicateModelState } from "@/modules/model/components";
import { useTrainingQuota } from "../../hooks/useTrainingQuota";
import {
  useCancelTrainingMutation,
  useDeleteModelMutation,
  useGetModelLogsQuery,
  useGetModelQuery,
  useTrainModelMutation,
} from "../../services";
import { ModelLayout } from "../ModelLayout";
import { ModelOutputs } from "../ModelOutputs";
import { ModelOverview } from "../ModelOverview";
import { ModelParameters } from "../ModelParameters";
import { ModelSubheader, type ModelTab } from "../ModelSubheader";
import { TrainingStartupScreen } from "../TrainingStartupScreen";
import { useApiErrorMessage } from "@/core/api";

export const ModelDetailPage = () => {
  const { t } = useTranslation(["model", "common"]);
  const apiErrorMessage = useApiErrorMessage();
  const navigate = useNavigate();
  const { projectId, modelId } = useParams();
  const [deleteModel, { isLoading: isDeleting }] = useDeleteModelMutation();
  const [trainModel] = useTrainModelMutation();
  const [cancelTraining, { isLoading: isCancelling }] = useCancelTrainingMutation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [activeTab, setActiveTab] = useState<ModelTab>("overview");
  const { isExhausted: isQuotaExhausted, label: quotaLabel } = useTrainingQuota();

  const {
    data: model,
    refetch: refetchModel,
    isLoading: isModelLoading,
  } = useGetModelQuery({
    projectId: Number(projectId),
    modelId: Number(modelId),
  });
  const {
    data: logs,
    refetch: refetchLogs,
    isLoading: isLogsLoading,
  } = useGetModelLogsQuery({
    projectId: Number(projectId),
    modelId: Number(modelId),
  });

  const isTraining = model?.status === ModelStatusEnum.TRAINING;
  // CONVERTING is no longer written — model.status covers training only, and
  // conversion progress is polled by the Outputs tab against its own rows.
  const isActive = isTraining;
  const isInitialLoading = isModelLoading || isLogsLoading;
  const isWaitingForLogs = isTraining && (!logs || logs.length === 0);

  useEffect(() => {
    if (!isTraining) return;
    const logsInterval = setInterval(() => refetchLogs(), 1000);
    return () => clearInterval(logsInterval);
  }, [isTraining, refetchLogs]);

  useEffect(() => {
    if (!isActive) return;
    const modelInterval = setInterval(() => refetchModel(), 3000);
    return () => clearInterval(modelInterval);
  }, [isActive, refetchModel]);

  const handleDuplicate = () => {
    if (!model) return;

    // Convert preprocessings with keepOriginal back to duplicate-image augmentations
    const preprocessingsAsAugs: AppliedAugmentation[] = (model.preprocessings ?? [])
      .filter((p) => !p.keepOriginal)
      .map((p) => ({
        id: crypto.randomUUID(),
        type: p.type,
        params: p.params as Record<string, number | boolean | string>,
      }));

    const duplicateAugs: AppliedAugmentation[] = (model.preprocessings ?? [])
      .filter((p) => p.keepOriginal)
      .map((p) => ({
        id: crypto.randomUUID(),
        type: p.type,
        params: { ...p.params, p: 1 } as Record<string, number | boolean | string>,
        duplicateImage: true,
      }));

    const normalAugs: AppliedAugmentation[] = (model.augmentations ?? []).map((a) => ({
      id: crypto.randomUUID(),
      type: a.type,
      params: a.params as Record<string, number | boolean | string>,
    }));

    const state: DuplicateModelState = {
      duplicateFrom: {
        epochs: model.epochs,
        trainingType: model.trainingType,
        backend: model.backend,
        annotationsUsed: model.annotationsUsed,
        labels: model.labels,
        outputs: model.outputTypes,
        region: model.region,
        datasetSplit: {
          train: model.splitTrain,
          validation: model.splitValidate,
          test: model.splitTest,
        },
        augmentations: [...normalAugs, ...duplicateAugs],
        preprocessings: preprocessingsAsAugs,
        customHyperparams:
          model.customHyperparams && Object.keys(model.customHyperparams).length > 0
            ? JSON.stringify(model.customHyperparams, null, 2)
            : "",
        taskIds: model.taskIds ?? [],
        datasetVersionId: model.datasetVersionId ?? null,
        useGroups: model.useGroups,
      },
    };

    navigate(`/projects/${projectId}/models/new`, { state });
  };

  if (isInitialLoading) {
    return (
      <ModelLayout>
        <div className="flex min-h-0 flex-1 flex-col gap-6">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-[40%]" />
            <div className="flex gap-2">
              <Skeleton className="h-10 w-24 rounded-md" />
              <Skeleton className="h-10 w-20 rounded-md" />
            </div>
          </div>
          <div className="flex gap-4">
            <Skeleton className="h-62.5 w-1/2 rounded-lg" />
            <Skeleton className="h-62.5 w-1/2 rounded-lg" />
          </div>
          <Skeleton className="min-h-0 flex-1 rounded-2xl" />
        </div>
      </ModelLayout>
    );
  }

  if (isWaitingForLogs) {
    return (
      <ModelLayout>
        {/* While the model is TRAINING with no logs yet, the last write to the
            row is the dispatch itself (status flip + batchJobName), so
            updatedAt is the training start — the elapsed clock survives
            reloads and reflects the job's real age. */}
        <TrainingStartupScreen
          modelName={model?.name}
          startedAt={model?.updatedAt}
        />
      </ModelLayout>
    );
  }

  const subheaderActions = (
    <>
      {model && (
        <Button variant="outline" size="sm" onClick={handleDuplicate}>
          <Copy className="mr-1 size-4" />
          {t("detail.duplicate")}
        </Button>
      )}
      {model?.status === ModelStatusEnum.DRAFT && (
        <>
          {quotaLabel && (
            <span className="text-xs text-muted-foreground">{quotaLabel}</span>
          )}
          <Button
            size="sm"
            disabled={isQuotaExhausted}
            onClick={async () => {
              try {
                await trainModel({
                  projectId: Number(projectId),
                  modelId: Number(modelId),
                }).unwrap();
              } catch (err: unknown) {
                toast.error(apiErrorMessage(err, t("detail.trainFailed")));
              }
            }}
          >
            {t("detail.train")}
          </Button>
        </>
      )}
      {isActive && (
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setShowCancelDialog(true)}
        >
          <Square className="mr-1 size-4" />
          {t("detail.stopTraining")}
        </Button>
      )}
      <Button
        variant="destructive"
        size="sm"
        className="p-2"
        onClick={() => setShowDeleteDialog(true)}
      >
        <Trash2 className="size-4" />
      </Button>
    </>
  );

  return (
    <ModelLayout>
      {/* Negate ModelLayout's p-6 on the top edge so the subheader is edge-to-edge. */}
      <div className="-mx-6 -mt-6 mb-4">
        <ModelSubheader
          activeTab={activeTab}
          onTabChange={setActiveTab}
          actions={subheaderActions}
        />
      </div>

      {/*
        `flex-1 min-h-0` forms the bounded-height chain so ModelLogs can use
        its own internal scroll. The bottom-gap at scroll-end is provided by
        a margin-bottom on ModelLogs itself (see ModelLogs.tsx) — margins
        extend the column's scrollHeight, so the gap appears after the
        overflowing content instead of being pinned inside the fixed-height
        wrapper.
      */}
      <div className="flex min-h-0 flex-1 flex-col">
        {activeTab === "overview" && (
          <ModelOverview
            modelName={model?.name}
            logs={logs}
            trainingType={model?.trainingType}
          />
        )}
        {activeTab === "outputs" && model && (
          <ModelOutputs model={model} projectId={Number(projectId)} />
        )}
        {activeTab === "parameters" && model && (
          <ModelParameters model={model} />
        )}
      </div>

      {showDeleteDialog && (
        <DeleteLimitDialog
          title={t("detail.deleteDialog.title")}
          description={t("detail.deleteDialog.description")}
          confirmLabel={t("detail.deleteDialog.confirm")}
          isLoading={isDeleting}
          onCancel={() => setShowDeleteDialog(false)}
          onConfirm={async () => {
            await deleteModel({
              projectId: Number(projectId),
              modelId: Number(modelId),
            }).unwrap();
            // The index route resolves to the newest remaining model, or the
            // new-version flow when this was the last one.
            navigate(`/projects/${projectId}/models`);
          }}
        />
      )}

      {showCancelDialog && (
        <DeleteLimitDialog
          title={t("detail.stopDialog.title")}
          description={t("detail.stopDialog.description")}
          confirmLabel={t("detail.stopDialog.confirm")}
          isLoading={isCancelling}
          onCancel={() => setShowCancelDialog(false)}
          onConfirm={async () => {
            await cancelTraining({
              projectId: Number(projectId),
              modelId: Number(modelId),
            }).unwrap();
            setShowCancelDialog(false);
          }}
        />
      )}
    </ModelLayout>
  );
};
